import * as DOMPurify from 'dompurify';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Spotlight } from 'api/spotlight/types';
import { ReactComponent as SpotlightSvg } from 'assets/svgs/spotlight.svg';
import { Avatar } from 'components/atoms/avatar';
import { Popover } from 'components/atoms/popover';
import { useGetUserByUserId } from 'hooks/use-idb';
import { fullName } from 'utils/full-name';
import { tryGetTextFromHTML } from 'utils/getTextFromHTML';
import { CpParaRegular } from '../customer-portal/components/typography/typo';
import { TaskName } from '../templates/commons/name-field';

const Content = styled.span``;

const Tag = styled.div`
  display: flex;
  width: fit-content;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-02) var(--spacing-06);
  padding-left: var(--spacing-03);
  border: 1px solid #f9d7af;
  cursor: pointer;

  background: white;
  border-radius: 100px;
  :hover {
    background: #f9d7af;
  }

  ${Content} {
    margin-left: var(--spacing-03);
  }
`;

const CountTag = styled(Tag)`
  padding-right: var(--spacing-03);
`;

export const SpotlightInTag: React.FC<{
  spotlight: Spotlight;
  onSpotlightClick: (s: Spotlight) => void;
}> = ({ spotlight, onSpotlightClick }) => {
  const user = useGetUserByUserId(spotlight?.createdBy);

  return (
    <Tag onClick={() => onSpotlightClick(spotlight)}>
      <SpotlightSvg />
      {user ? (
        <Avatar size="small" src={user?.photoUrl} name={fullName(user)} />
      ) : (
        <></>
      )}
      <Content>
        {spotlight?.caption.length > 20 ? (
          <>{tryGetTextFromHTML(spotlight?.caption).slice(0, 20)}...</>
        ) : (
          <TaskName taskName={spotlight?.caption} />
        )}
      </Content>
    </Tag>
  );
};

const StyledAvatar = styled(Avatar)``;

const SpotlightAsMessageWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-radius: var(--border-radius-default);
  max-width: 350px;
  flex-direction: column;

  padding: var(--spacing-05);
  background: #fef7f0;

  ${StyledAvatar} {
    margin-right: var(--spacing-05);
  }
`;

const SpotlightUserInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  ${CpParaRegular} {
    color: black;
  }
`;

const SpotlightContentWrapper = styled.div`
  display: flex;
  align-items: start;
  justify-content: space-between;
  width: 100%;
`;

const SpotlightAsMessage: React.FC<{
  spotlight: Spotlight;
  onSpotlightClick: (s: Spotlight) => void;
}> = ({ spotlight, onSpotlightClick }) => {
  const user = useGetUserByUserId(spotlight?.createdBy);

  const formattedDate = useMemo(() => {
    const createdAt = moment(spotlight?.createdAt);

    if (createdAt.isSame(moment(), 'year')) {
      return createdAt.format('DD MMM, HH:mm A');
    }

    return createdAt.format('DD MMM YY, HH:mm A');
  }, [spotlight?.createdAt]);

  return (
    <SpotlightAsMessageWrapper onClick={() => onSpotlightClick(spotlight)}>
      <SpotlightUserInfoWrapper>
        <CpParaRegular>{user ? fullName(user) : ''}</CpParaRegular>
        <CpParaRegular>{formattedDate}</CpParaRegular>
      </SpotlightUserInfoWrapper>
      <SpotlightContentWrapper>
        {user ? (
          <StyledAvatar
            size="small"
            src={user?.photoUrl}
            name={fullName(user)}
          />
        ) : (
          <></>
        )}
        <Content>
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              // eslint-disable-next-line @typescript-eslint/naming-convention
              __html: DOMPurify.sanitize(spotlight?.caption),
            }}
          />
        </Content>
      </SpotlightContentWrapper>
    </SpotlightAsMessageWrapper>
  );
};

const SpotlightGroupWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;

  ${Tag} {
    margin-right: var(--spacing-02);
  }
`;

const SpotlightMessageWrapperInList = styled.div`
  ${SpotlightAsMessageWrapper} {
    margin-bottom: var(--spacing-03);
  }
`;

const SpotlightMessageListPopover = styled(Popover)`
  ${SpotlightMessageWrapperInList} {
    padding: var(--spacing-03);
    padding-bottom: var(--spacing-01);
  }
`;

export const SpotlightGroup: React.FC<{
  spotlights: Spotlight[];
  onSpotlightClick: (s: Spotlight) => void;
}> = ({ spotlights, onSpotlightClick }) => {
  // a number that says how maby spotlights are rendered as tags and how many are rendered as messages.
  // the number shoule be based on the width of the container.
  // if the container is too small, then all spotlights should be rendered as messages.
  // if the container is big enough, then count number of spotlights should be rendered as tags and the rest as messages.
  // if the container is big enough, then all spotlights should be rendered as tags.
  const [count, setCount] = useState(2);

  const firstTwoSpotlights = useMemo(
    () => (spotlights.length > count ? spotlights.slice(0, count) : spotlights),
    [count, spotlights],
  );

  const remainingSpotlights = useMemo(
    () => (spotlights.length > count ? spotlights.slice(count) : []),
    [count, spotlights],
  );

  return (
    <SpotlightGroupWrapper
      ref={(_ref) => {
        setCount((_ref?.clientWidth || 0) / 230);
      }}
    >
      {firstTwoSpotlights.map((spotlight) => (
        <SpotlightMessageListPopover
          getPopupContainer={() =>
            document.querySelector('.rl-cp-page') || document.body
          }
          placement="bottomLeft"
          content={
            <SpotlightAsMessage
              onSpotlightClick={onSpotlightClick}
              key={spotlight?.customerResourcePinnedMappingId}
              spotlight={spotlight}
            />
          }
        >
          <span>
            <SpotlightInTag
              onSpotlightClick={onSpotlightClick}
              key={spotlight?.customerResourcePinnedMappingId}
              spotlight={spotlight}
            />
          </span>
        </SpotlightMessageListPopover>
      ))}
      {remainingSpotlights.length > 0 ? (
        <SpotlightMessageListPopover
          getPopupContainer={() =>
            document.querySelector('.rl-cp-page') || document.body
          }
          placement="bottomLeft"
          content={
            <SpotlightMessageWrapperInList>
              {remainingSpotlights.map((spotlight) => (
                <SpotlightAsMessage
                  onSpotlightClick={onSpotlightClick}
                  key={spotlight?.customerResourcePinnedMappingId}
                  spotlight={spotlight}
                />
              ))}
            </SpotlightMessageWrapperInList>
          }
        >
          <span>
            <CountTag>+{remainingSpotlights.length}</CountTag>
          </span>
        </SpotlightMessageListPopover>
      ) : (
        <></>
      )}
    </SpotlightGroupWrapper>
  );
};
